import { useEffect, useState } from "react"
import {
    combineLatest,
    concat,
    defer,
    from,
    iif,
    merge,
    of,
    partition,
    Subject,
} from "rxjs"
import {
    filter,
    map,
    mergeMap,
    pairwise,
    scan,
    share,
    startWith,
    switchMap,
    takeUntil,
    takeWhile,
    toArray,
    withLatestFrom,
} from "rxjs/operators"
import { ConflictResolveType } from "../context/task"
import { Drive } from "../grpc"
import { IsExistsRequest, PathMessage } from "../protocol/drive_pb"
import useAuthGRPC from "./useAuthGRPC"

export interface Path {
    source: string
    destination: string
}

export interface TaskPathParams {
    paths: Path[]
    task?(paths: Path[]): void
}

export interface ResolveWay {
    method: ConflictResolveType
    applyAll?: boolean
}

export default function useResolveTask() {
    const isExist = useAuthGRPC(Drive.isExists, Drive)
    const resolveName = useAuthGRPC(Drive.resolveName, Drive)

    const [conflictPaths, setConflictPaths] = useState<Path[]>([])
    const [processTask$] = useState(new Subject<TaskPathParams>())
    const [resolve$] = useState(new Subject<ResolveWay>())

    useEffect(() => {
        const bye$ = new Subject<void>()
        const resolving$ = processTask$.pipe(
            mergeMap((task) => {
                const { paths } = task
                const destinations = paths.map((p) => p.destination)
                const req = new IsExistsRequest()
                req.setPathList(destinations)
                const existed$ = defer(() =>
                    isExist(req).pipe(map((res) => res.getExistsList())),
                )
                return existed$.pipe(
                    switchMap((existedPaths) =>
                        of(
                            partition(from(paths), (path) =>
                                existedPaths.includes(path.destination),
                            ),
                        ),
                    ),
                    map(([conflicted$, nonConflicted$]) => {
                        const conflictedPaths$ = conflicted$.pipe(toArray())
                        const remaining$ = conflictedPaths$.pipe(
                            switchMap((conflicted) =>
                                concat(
                                    of(conflicted),
                                    resolve$.pipe(
                                        scan(
                                            (acc, c) =>
                                                c.applyAll ? [] : acc.slice(1),
                                            conflicted,
                                        ),
                                    ),
                                ).pipe(
                                    startWith([]),
                                    pairwise(),
                                    takeWhile(
                                        (remaining) => remaining.length > 0,
                                    ),
                                ),
                            ),
                        )
                        const override$ = resolve$.pipe(
                            filter(
                                (resolve) =>
                                    resolve.method ===
                                    ConflictResolveType.Overwrite,
                            ),
                            withLatestFrom(remaining$),
                            takeWhile(
                                ([, [remaining]]) => remaining.length > 0,
                            ),
                            map(([resolve, [remaining]]) =>
                                !!resolve.applyAll ? remaining : [remaining[0]],
                            ),
                        )
                        const keepboth$ = resolve$.pipe(
                            filter(
                                (resolve) =>
                                    resolve.method ===
                                    ConflictResolveType.Keepboth,
                            ),
                            withLatestFrom(remaining$),
                            takeWhile(
                                ([, [remaining]]) => remaining.length > 0,
                            ),
                            mergeMap(([resolve, [remaining]]) =>
                                iif(
                                    () => !!resolve.applyAll,
                                    from(remaining),
                                    of(remaining[0]),
                                ).pipe(
                                    mergeMap((path) => {
                                        const req = new PathMessage()
                                        req.setPath(path.destination)
                                        return resolveName(req).pipe(
                                            map((res) => ({
                                                ...path,
                                                destination: res.getPath(),
                                            })),
                                        )
                                    }),
                                    toArray(),
                                ),
                            ),
                        )
                        return {
                            task,
                            doTask$: merge(
                                nonConflicted$.pipe(toArray()),
                                override$,
                                keepboth$,
                            ).pipe(filter((paths) => paths.length > 0)),
                            conflictPaths$: remaining$.pipe(
                                map(([, remaining]) => remaining),
                            ),
                        }
                    }),
                )
            }, 1),
            share(),
        )
        resolving$
            .pipe(
                mergeMap(({ task: { task }, doTask$ }) =>
                    combineLatest([of(task), doTask$]),
                ),
                takeUntil(bye$),
            )
            .subscribe(([task, paths]) => task?.(paths))
        resolving$
            .pipe(
                mergeMap(({ conflictPaths$ }) => conflictPaths$),
                takeUntil(bye$),
            )
            .subscribe(setConflictPaths)
        return () => bye$.next()
    }, [isExist, resolveName, processTask$, resolve$])

    return {
        conflictPaths,
        processTask$,
        resolve$,
    }
}
